/**
 * Centralized environment configuration
 *
 * NEVER access import.meta.env directly elsewhere - always import from here.
 *
 * Build-time configuration via Vite:
 * - Development: Set VITE_API_URL in .env file (e.g., http://localhost:8000)
 * - Production: VITE_API_URL='' (empty), use relative URLs via ingress
 */

// Get raw value - will be '' in production, 'http://localhost:8000' in dev
const rawApiUrl = import.meta.env.VITE_API_URL as string | undefined;

// API_BASE: Empty string in production = relative URLs via ingress
// In development, set via .env file
export const API_BASE: string = rawApiUrl ?? '';

// Fail loud in production if localhost leaks through
if (import.meta.env.PROD && API_BASE.includes('localhost')) {
  console.error(
    '[FATAL CONFIG ERROR] API_BASE contains "localhost" in production!\n' +
    'VITE_API_URL was not set correctly at build time.\n' +
    'Expected: VITE_API_URL="" (empty string for relative URLs)'
  );
}

/**
 * Base URL of the public data API (`/v1`) — what copyable snippets should
 * tell the user to POST to, which is not necessarily where the SPA talks.
 *
 * Dev: same origin as the platform API (VITE_API_URL, e.g. localhost:8000).
 * Deployed: a dedicated host routed straight to the API service
 * (infra/pulumi-v2 ingress) — `api-<env>` for the test and dev stacks,
 * `api` for prod and every vanity org host.
 */
const PUBLIC_API_HOST_BY_PLATFORM_HOST: Record<string, string> = {
  'test.trainloop.ai': 'https://api-test.trainloop.ai',
  'dev.trainloop.ai': 'https://api-dev.trainloop.ai',
};

function resolvePublicApiBase(): string {
  if (API_BASE) return API_BASE;
  if (typeof window === 'undefined') return 'https://api.trainloop.ai';
  const { hostname, origin } = window.location;
  if (!hostname.endsWith('.trainloop.ai')) return origin;
  return PUBLIC_API_HOST_BY_PLATFORM_HOST[hostname] ?? 'https://api.trainloop.ai';
}

export const PUBLIC_API_BASE: string = resolvePublicApiBase();

// Development mode flag
export const IS_DEV: boolean = import.meta.env.DEV;

// Default dev team ID for local development without auth
export const DEV_TEAM_ID = '11111111-1111-1111-1111-111111111111';
